import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Loader2, Save } from "lucide-react";
import { API, authHdr } from "./AssociateFields";
import { ACTIONS, RIGHTS_CATALOG, fullRights } from "../../lib/rights";

const merge = (r) => {
  const base = fullRights();
  Object.entries(r || {}).forEach(([k, v]) => { if (base[k]) base[k] = { ...base[k], ...v, subs: { ...base[k].subs, ...(v.subs || {}) } }; });
  return base;
};

export const RightsTab = ({ id, ro, employeeId }) => {
  const [data, setData] = useState(null);
  const [who, setWho] = useState("");
  const [rights, setRights] = useState(fullRights());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/associate-extras/${id}/rights`, { headers: authHdr() }).then((r) => {
      setData(r.data);
      const emp = employeeId && r.data.employees.find((e) => e.id === employeeId);
      if (emp) setWho(employeeId);
      setRights(merge(emp ? emp.rights : r.data.rights));
    })
      .catch(() => toast.error("Could not load rights"));
  }, [id]);
  const pick = (v) => { setWho(v); setRights(merge(v ? data.employees.find((e) => e.id === v)?.rights : data.rights)); };
  const tick = (tab, key, val, sub) => setRights((r) => {
    const t = { ...r[tab] };
    if (sub) t.subs = { ...t.subs, [key]: val }; else t[key] = val;
    if (!sub && key === "view" && !val) ACTIONS.forEach((a) => { t[a] = false; });
    return { ...r, [tab]: t };
  });
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/associate-extras/${id}/rights`, { rights, employee_id: who || undefined }, { headers: authHdr() });
      toast.success("Rights saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  if (!data) return <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <section className="space-y-4" data-testid="associate-rights-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-teal-700">Rights & Authorization</h3>
          <p className="mt-1 text-xs text-gray-500">Untick what this {who ? "employee" : "associate"} should not see or do. Employees can never get more than their associate.</p>
        </div>
        {!employeeId && <select className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm" value={who} onChange={(e) => pick(e.target.value)} data-testid="rights-who-select">
          <option value="">Associate (all their logins)</option>
          {data.employees.map((e) => <option key={e.id} value={e.id}>Employee — {e.name || e.email}</option>)}
        </select>}
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm" data-testid="rights-table">
          <thead><tr className="border-b bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
            <th className="px-3 py-2">Tab</th>{ACTIONS.map((a) => <th key={a} className="px-3 py-2 text-center">{a}</th>)}</tr></thead>
          <tbody>{RIGHTS_CATALOG.map((t) => (
            <tr key={t.key} className="border-b align-top last:border-0">
              <td className="px-3 py-2.5">
                <div className="font-medium text-gray-800">{t.label}</div>
                {t.subs.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">{t.subs.map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <Checkbox checked={rights[t.key].subs[k] !== false} disabled={ro || !rights[t.key].view}
                        onCheckedChange={(c) => tick(t.key, k, !!c, true)} data-testid={`rights-${t.key}-sub-${k}`} />{label}
                    </label>))}</div>
                )}
              </td>
              {ACTIONS.map((a) => (
                <td key={a} className="px-3 py-2.5 text-center">
                  <Checkbox checked={rights[t.key][a] !== false} disabled={ro || (a !== "view" && !rights[t.key].view)}
                    onCheckedChange={(c) => tick(t.key, a, !!c)} data-testid={`rights-${t.key}-${a}`} />
                </td>))}
            </tr>))}</tbody>
        </table>
      </div>
      {!ro && <div className="flex justify-end"><Button className="bg-[#1a7f6f] hover:bg-[#166a5c]" onClick={save} disabled={busy} data-testid="rights-save-btn">
        {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Save rights</Button></div>}
      {ro && <p className="text-xs text-gray-500">Use Edit from the associate's action menu to change rights.</p>}
    </section>
  );
};
